import { cn } from "./utils";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

const badgeVariants = cva(
    "inline-flex items-center gap-1.5 px-2.5 py-0.5 border rounded-full font-medium text-xs transition-colors",
    {
        variants: {
            variant: {
                default:
                    "border-transparent bg-indigo-800 text-indigo-50 hover:bg-indigo-900/80 dark:bg-indigo-50 dark:text-indigo-900 dark:hover:bg-indigo-50/80 focus:outline-none focus:ring-2 focus:ring-indigo-950 dark:focus:ring-indigo-300 focus:ring-offset-2 transition-colors",
                secondary:
                    "border-transparent bg-indigo-100 text-indigo-900 hover:bg-indigo-100/80 dark:bg-indigo-800 dark:text-indigo-50 dark:hover:bg-indigo-800/80 focus:outline-none focus:ring-2 focus:ring-indigo-950 dark:focus:ring-indigo-300 focus:ring-offset-2 transition-colors",
                destructive:
                    "border-transparent bg-red-500 text-indigo-50 hover:bg-red-500/80 dark:bg-red-900 dark:text-indigo-50 dark:hover:bg-red-900/80 focus:outline-none focus:ring-2 focus:ring-indigo-950 dark:focus:ring-indigo-300 focus:ring-offset-2 transition-colors",
                outline:
                    "text-indigo-950 dark:text-indigo-50 hover:bg-slate-50 border-indigo-200 dark:border-indigo-800 focus:outline-none focus:ring-2 focus:ring-indigo-950 dark:focus:ring-indigo-300 focus:ring-offset-2 transition-colors",
                // Neutral shell for tinted badges via `color` (avoids indigo secondary hover)
                static: "cursor-default",
            },
            color: {
                pink: "border-pink-300 bg-pink-50 text-pink-700 hover:bg-pink-100",
                rose: "border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100",
                fuchsia: "border-fuchsia-300 bg-fuchsia-50 text-fuchsia-700 hover:bg-fuchsia-100",
                purple: "border-purple-300 bg-purple-50 text-purple-700 hover:bg-purple-100",
                violet: "border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100",
                indigo: "border-indigo-300 bg-indigo-50 text-indigo-700 hover:bg-indigo-100",
                blue: "border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100",
                sky: "border-sky-300 bg-sky-50 text-sky-700 hover:bg-sky-100",
                cyan: "border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100",
                teal: "border-teal-300 bg-teal-50 text-teal-700 hover:bg-teal-100",
                emerald: "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100",
                green: "border-green-300 bg-green-50 text-green-700 hover:bg-green-100",
                lime: "border-lime-300 bg-lime-50 text-lime-700 hover:bg-lime-100",
                yellow: "border-yellow-300 bg-yellow-50 text-yellow-700 hover:bg-yellow-100",
                amber: "border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-100",
                orange: "border-orange-300 bg-orange-50 text-orange-700 hover:bg-orange-100",
                red: "border-red-300 bg-red-50 text-red-700 hover:bg-red-100",
                gray: "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100",
                slate: "border-slate-300 bg-slate-50 text-slate-700 hover:bg-slate-100",
            },
        },

        defaultVariants: {
            variant: "default",
        },
    }
);

export interface BadgeProps
    extends Omit<React.HTMLAttributes<HTMLDivElement>, "color">, VariantProps<typeof badgeVariants> {}

function Badge({ className, variant, color, ...props }: BadgeProps) {
    return <div className={cn(badgeVariants({ variant, color }), className)} {...props} />;
}

export { Badge, badgeVariants };
